import React from 'react';
import {
  Box,
  Paper,
  Typography,
  Card,
  CardContent,
  CardActions,
  Button,
  Chip,
  Stack,
  LinearProgress,
} from '@mui/material';
import {
  Email as EmailIcon,
  Phone as PhoneIcon,
  Business as BusinessIcon,
  Visibility as VisibilityIcon,
} from '@mui/icons-material';
import { SearchResult } from '../../types/search.types';
import { useNavigate } from 'react-router-dom';

interface SearchResultsProps {
  results: SearchResult[];
  isLoading: boolean;
}

export const SearchResults: React.FC<SearchResultsProps> = ({ results, isLoading }) => {
  const navigate = useNavigate();

  if (isLoading) {
    return (
      <Paper sx={{ p: 3 }}>
        <Typography variant='body1' gutterBottom>
          Searching leads...
        </Typography>
        <LinearProgress />
      </Paper>
    );
  }

  if (results.length === 0) {
    return (
      <Paper sx={{ p: 4, textAlign: 'center' }}>
        <Typography variant='h6' color='text.secondary'>
          No results found
        </Typography>
        <Typography variant='body2' color='text.secondary' sx={{ mt: 1 }}>
          Try adjusting your search query or filters
        </Typography>
      </Paper>
    );
  }

  return (
    <Box>
      <Typography variant='h6' gutterBottom>
        Found {results.length} matching lead{results.length !== 1 ? 's' : ''}
      </Typography>
      <Box sx={{ display: 'grid', gap: 2 }}>
        {results.map((result) => (
          <Card key={result.lead_id} sx={{ position: 'relative' }}>
            <CardContent>
              <Stack direction='row' sx={{ justifyContent: 'space-between', alignItems: 'start', mb: 2 }}>
                <Box>
                  <Typography variant='h6'>
                    {result.lead.first_name} {result.lead.last_name}
                  </Typography>
                  {result.lead.position && result.lead.company && (
                    <Typography variant='body2' color='text.secondary'>
                      {result.lead.position} at {result.lead.company}
                    </Typography>
                  )}
                </Box>
                <Chip
                  label={`${(result.similarity * 100).toFixed(0)}% match`}
                  color={result.similarity > 0.8 ? 'success' : result.similarity > 0.7 ? 'primary' : 'default'}
                  size='small'
                />
              </Stack>

              <Stack direction='row' spacing={2} sx={{ mb: 2, flexWrap: 'wrap' }}>
                {result.lead.email && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <EmailIcon fontSize='small' color='action' />
                    <Typography variant='body2'>{result.lead.email}</Typography>
                  </Box>
                )}
                {result.lead.phone && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <PhoneIcon fontSize='small' color='action' />
                    <Typography variant='body2'>{result.lead.phone}</Typography>
                  </Box>
                )}
                {result.lead.company && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <BusinessIcon fontSize='small' color='action' />
                    <Typography variant='body2'>{result.lead.company}</Typography>
                  </Box>
                )}
              </Stack>

              <Stack direction='row' spacing={1} sx={{ mb: 2 }}>
                <Chip label={result.lead.status} size='small' color='primary' variant='outlined' />
                <Chip label={result.lead.priority} size='small' color='secondary' variant='outlined' />
              </Stack>

              {result.lead.notes && (
                <Typography variant='body2' color='text.secondary' sx={{ 
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                }}>
                  {result.lead.notes}
                </Typography>
              )}
            </CardContent>
            <CardActions>
              <Button
                size='small'
                startIcon={<VisibilityIcon />}
                onClick={() => navigate(`/leads/${result.lead_id}`)}
              >
                View Details
              </Button>
            </CardActions>
          </Card>
        ))}
      </Box>
    </Box>
  );
};